import React, { useMemo, useState } from 'react';
import { flightSearchService } from '../../../services/flightSearchService';
import AirportAutocompleteInput from './AirportAutocompleteInput';
import './FlightSearchModal.css';

const DEFAULT_FILTERS = { maxStops: 'any', airline: 'any', minPrice: '', maxPrice: '' };
const IATA_CODE_PATTERN = /^[A-Za-z]{3}$/;

const formatDurationMinutes = (minutes) => {
  if (minutes === null || minutes === undefined || isNaN(minutes)) return null;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours > 0 ? `${hours}h ` : ''}${mins}m`.trim();
};

// "(1 stop via DEN, 2h 26m)" / "(2 stops via DEN, ORD)" - falls back to
// just the stop count if connection detail isn't available for some reason.
const formatStopsText = (leg) => {
  if (!leg.stops || leg.stops === 0) return '';
  if (!leg.connections || leg.connections.length === 0) {
    return ` (${leg.stops} stop${leg.stops > 1 ? 's' : ''})`;
  }
  const via = leg.connections.map((c) => {
    const duration = formatDurationMinutes(c.durationMinutes);
    return duration ? `${c.airport} (${duration})` : c.airport;
  }).join(', ');
  return ` (${leg.stops} stop${leg.stops > 1 ? 's' : ''} via ${via})`;
};

// AirportAutocompleteInput lets an agent submit either a picked suggestion
// (already a clean 3-letter IATA code) or free-typed text they never
// clicked a suggestion for (e.g. a full city name). Send the former as
// origin/destination directly; send the latter as originName/
// destinationName so the backend resolves it server-side (same Duffel
// suggestions lookup) instead of erroring on an invalid IATA code.
const toLocationParams = (value, paramName) => {
  const trimmed = (value || '').trim();
  return IATA_CODE_PATTERN.test(trimmed)
    ? { [paramName]: trimmed.toUpperCase() }
    : { [`${paramName}Name`]: trimmed };
};

// Launched from FlightForm's "Search Flights" button. Searches live flight
// offers via the backend's Duffel integration (duffelFlightService) and
// lets the agent import a result, which prefills FlightForm's fields -
// still fully editable before saving. See componentFormRegistry.js for how
// FlightForm's onSave payload is turned into a saved quote component.
//
// Stops/airline/price filters below are applied client-side over the
// current result set (Duffel already returns up to ~20+ offers per
// search) - no extra API calls needed to filter.
const FlightSearchModal = ({ onClose, onImport }) => {
  const [searchParams, setSearchParams] = useState({
    origin: '',
    destination: '',
    departDate: '',
    returnDate: '',
    tripType: 'one_way',
    adults: 1
  });
  const [results, setResults] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setSearchParams((prev) => ({ ...prev, [field]: value }));
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setResults(null);
    setFilters(DEFAULT_FILTERS);

    if (!searchParams.origin || !searchParams.destination || !searchParams.departDate) {
      setError('Origin, destination, and departure date are required.');
      return;
    }

    setLoading(true);
    try {
      const response = await flightSearchService.searchFlights({
        ...toLocationParams(searchParams.origin, 'origin'),
        ...toLocationParams(searchParams.destination, 'destination'),
        departDate: searchParams.departDate,
        returnDate: searchParams.tripType === 'round_trip' ? searchParams.returnDate : undefined,
        adults: parseInt(searchParams.adults, 10) || 1
      });

      if (response.success) {
        setResults(response.data.flights || []);
      } else {
        setError(response.error || 'Flight search failed. Please try again or enter details manually.');
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
        'Flight search is currently unavailable. Please enter flight details manually.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Distinct airlines across the current result set, for the airline filter.
  const airlineOptions = useMemo(() => {
    if (!results) return [];
    const airlines = new Set();
    results.forEach((flight) => {
      if (flight.outbound?.airline) airlines.add(flight.outbound.airline);
      if (flight.inbound?.airline) airlines.add(flight.inbound.airline);
    });
    return Array.from(airlines).sort();
  }, [results]);

  // A round-trip offer's "stops" is really two numbers (outbound/inbound
  // legs) - for the stops filter we treat the WORST (highest) leg as the
  // offer's stop count, so e.g. "Nonstop" only shows offers with no
  // connections on either leg.
  const maxLegStops = (flight) => Math.max(
    flight.outbound?.stops || 0,
    flight.inbound?.stops || 0
  );

  const filteredResults = useMemo(() => {
    if (!results) return [];
    return results.filter((flight) => {
      if (filters.maxStops !== 'any') {
        const stops = maxLegStops(flight);
        if (filters.maxStops === '0' && stops !== 0) return false;
        if (filters.maxStops === '1' && stops > 1) return false;
        if (filters.maxStops === '2+' && stops < 2) return false;
      }
      if (filters.airline !== 'any') {
        const matchesAirline = flight.outbound?.airline === filters.airline || flight.inbound?.airline === filters.airline;
        if (!matchesAirline) return false;
      }
      if (filters.minPrice && flight.price.total < parseFloat(filters.minPrice)) return false;
      if (filters.maxPrice && flight.price.total > parseFloat(filters.maxPrice)) return false;
      return true;
    });
  }, [results, filters]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content flight-search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Search Flights</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <form className="flight-search-form" onSubmit={handleSearch}>
            <div className="form-group">
              <label>Trip Type</label>
              <select
                value={searchParams.tripType}
                onChange={(e) => handleChange('tripType', e.target.value)}
                className="form-control"
              >
                <option value="one_way">One-Way</option>
                <option value="round_trip">Round-Trip</option>
              </select>
            </div>

            <div className="form-row">
              <AirportAutocompleteInput
                label="From*"
                value={searchParams.origin}
                onChange={(code) => handleChange('origin', code)}
                placeholder="City or airport, e.g. Orlando / MCO"
                required
              />
              <AirportAutocompleteInput
                label="To*"
                value={searchParams.destination}
                onChange={(code) => handleChange('destination', code)}
                placeholder="City or airport, e.g. Los Angeles / LAX"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Departure Date*</label>
                <input
                  type="date"
                  value={searchParams.departDate}
                  onChange={(e) => handleChange('departDate', e.target.value)}
                  required
                />
              </div>
              {searchParams.tripType === 'round_trip' && (
                <div className="form-group">
                  <label>Return Date*</label>
                  <input
                    type="date"
                    value={searchParams.returnDate}
                    onChange={(e) => handleChange('returnDate', e.target.value)}
                    required
                  />
                </div>
              )}
              <div className="form-group">
                <label>Passengers</label>
                <input
                  type="number"
                  min="1"
                  max="9"
                  value={searchParams.adults}
                  onChange={(e) => handleChange('adults', e.target.value)}
                />
              </div>
            </div>

            {error && <p className="flight-search-error">{error}</p>}

            <div className="flight-search-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Searching...' : 'Search Flights'}
              </button>
            </div>
          </form>

          {results !== null && (
            <div className="flight-search-results">
              {results.length === 0 ? (
                <p className="no-flights">No flights found for these dates. Try different dates or enter details manually.</p>
              ) : (
                <>
                  <div className="flight-filters">
                    <div className="flight-filter-group">
                      <label>Stops</label>
                      <select
                        value={filters.maxStops}
                        onChange={(e) => handleFilterChange('maxStops', e.target.value)}
                      >
                        <option value="any">Any</option>
                        <option value="0">Nonstop</option>
                        <option value="1">1 stop or fewer</option>
                        <option value="2+">2+ stops</option>
                      </select>
                    </div>
                    <div className="flight-filter-group">
                      <label>Airline</label>
                      <select
                        value={filters.airline}
                        onChange={(e) => handleFilterChange('airline', e.target.value)}
                      >
                        <option value="any">All Airlines</option>
                        {airlineOptions.map((airline) => (
                          <option key={airline} value={airline}>{airline}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flight-filter-group">
                      <label>Min Price ($)</label>
                      <input
                        type="number"
                        min="0"
                        placeholder="0"
                        value={filters.minPrice}
                        onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                      />
                    </div>
                    <div className="flight-filter-group">
                      <label>Max Price ($)</label>
                      <input
                        type="number"
                        min="0"
                        placeholder="Any"
                        value={filters.maxPrice}
                        onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                      />
                    </div>
                  </div>

                  <p className="flight-results-count">
                    Showing {filteredResults.length} of {results.length} flight{results.length === 1 ? '' : 's'}
                  </p>

                  {filteredResults.length === 0 ? (
                    <p className="no-flights">No flights match these filters. Try widening them.</p>
                  ) : (
                    filteredResults.map((flight) => (
                      <div key={flight.offerId} className="flight-result-item">
                        <div className="flight-result-main">
                          <div className="flight-result-leg">
                            <strong>{flight.outbound.airline}</strong> {flight.outbound.flightNumber}
                            <span className="flight-route">
                              {flight.outbound.departureAirport} {flight.outbound.departureTime} → {flight.outbound.arrivalAirport} {flight.outbound.arrivalTime}
                              {formatStopsText(flight.outbound)}
                            </span>
                          </div>
                          {flight.inbound && (
                            <div className="flight-result-leg">
                              <strong>{flight.inbound.airline}</strong> {flight.inbound.flightNumber}
                              <span className="flight-route">
                                {flight.inbound.departureAirport} {flight.inbound.departureTime} → {flight.inbound.arrivalAirport} {flight.inbound.arrivalTime}
                                {formatStopsText(flight.inbound)}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flight-result-side">
                          <div className="flight-result-price">${flight.price.total.toFixed(2)}</div>
                          <div className="flight-result-price-note">
                            total for {searchParams.adults} passenger{parseInt(searchParams.adults, 10) > 1 ? 's' : ''}
                          </div>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => onImport(flight)}
                          >
                            Import
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlightSearchModal;
